import type { ReactNode } from 'react';
import type { FormikProps } from 'formik';
import { message } from 'antd';
import { Input } from '../../../components/ui';
import { UploadIcon } from '../../../assets/icons';
import { useUploadS3Mutation } from '../../../features/uploads/uploadApi';
import type { OrgFormValues } from './types';

const MAX_LOGO_BYTES = 5 * 1024 * 1024; // 5MB, matching the dropzone copy.

const TEXTAREA =
  'w-full resize-none rounded-lg border border-line-soft bg-white px-3.5 py-3 text-sm text-ink placeholder:text-[#94a3b8] transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15';

function Card({
  title,
  subtitle,
  headerRight,
  children,
}: {
  title: string;
  subtitle?: string;
  headerRight?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-line-soft bg-white shadow-card">
      <div className="flex items-center justify-between gap-4 border-b border-line-soft px-6 py-4">
        <div className="flex flex-col gap-0.5">
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          {subtitle && <p className="text-xs text-ink-secondary">{subtitle}</p>}
        </div>
        {headerRight}
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

function Field({
  label,
  htmlFor,
  required,
  error,
  className = '',
  children,
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  error?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={['flex flex-col gap-1.5', className].join(' ')}>
      <label htmlFor={htmlFor} className="text-xs font-medium text-[#334155]">
        {label}
        {required && <span className="text-[#ef4444]"> *</span>}
      </label>
      {children}
      {error && <p className="text-xs font-medium text-[#ba1a1a]">{error}</p>}
    </div>
  );
}

/** Step 1 — Organization Identity + Location & Contact (single-column, two cards). */
export default function OrganizationDetailsStep({ formik }: { formik: FormikProps<OrgFormValues> }) {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } = formik;
  const [uploadS3, { isLoading: uploadingLogo }] = useUploadS3Mutation();

  // Upload the picked logo to S3 and keep the returned URL for the payload.
  const handleLogoChange = async (file: File | null) => {
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      message.error('Logo must be 5MB or smaller.');
      return;
    }
    setFieldValue('logo', file);
    try {
      const res = await uploadS3(file).unwrap();
      setFieldValue('logoUrl', res.url);
    } catch {
      message.error('Logo upload failed. Please try again.');
      setFieldValue('logo', null);
      setFieldValue('logoUrl', '');
    }
  };

  // Only show a field's error once it has been touched (or after a submit attempt).
  const errorOf = (name: keyof OrgFormValues) =>
    touched[name] && errors[name] ? (errors[name] as string) : undefined;
  const invalid = (name: keyof OrgFormValues) => (errorOf(name) ? 'error' : '');
  const textareaCls = (name: keyof OrgFormValues) =>
    [TEXTAREA, errorOf(name) ? 'border-red-400 focus:border-red-500 focus:ring-red-100' : ''].join(
      ' ',
    );

  return (
    <div className="flex flex-col gap-6">
      {/* Card 1 — Organization Identity (with Status segmented control in the header) */}
      <Card
        title="Organization Identity"
        subtitle="Specify core identifiers, active state, and brand logo."
        headerRight={
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-[#475569]">Status:</span>
            <div className="flex items-center gap-0.5 rounded-lg border border-line-soft bg-brand-shade p-[5px]">
              {(['active', 'inactive'] as const).map((s) => {
                const selected = values.status === s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setFieldValue('status', s)}
                    className={[
                      'rounded-md px-3 py-1 text-xs capitalize transition-colors',
                      selected
                        ? 'border border-[#bfdbfe]/50 bg-white font-semibold text-brand'
                        : 'font-medium text-[#475569] hover:text-brand',
                    ].join(' ')}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
        }
      >
        <div className="flex flex-col gap-5">
          <Field label="Organization Name" htmlFor="name" required error={errorOf('name')}>
            <Input
              id="name"
              name="name"
              placeholder="e.g. Municipal Governance Department"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              status={invalid('name')}
            />
          </Field>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Short Name" htmlFor="shortName" required error={errorOf('shortName')}>
              <Input
                id="shortName"
                name="shortName"
                placeholder="e.g. MGD"
                value={values.shortName}
                onChange={handleChange}
                onBlur={handleBlur}
                status={invalid('shortName')}
              />
            </Field>
            <Field label="Internal Code" htmlFor="internalCode">
              <Input
                id="internalCode"
                name="internalCode"
                placeholder="Auto-generated on save"
                value={values.internalCode}
                readOnly
                disabled
                title="Generated automatically by the system"
                className="cursor-not-allowed bg-[#f1f5f9] text-ink-secondary"
              />
            </Field>
          </div>

          <Field label="Description" htmlFor="description" error={errorOf('description')}>
            <textarea
              id="description"
              name="description"
              rows={3}
              placeholder="Brief summary of the organization's primary functions..."
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              className={textareaCls('description')}
            />
          </Field>

          {/* Organization Logo — dashed dropzone */}
          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold text-ink">Organization Logo</span>
            <label
              className={[
                'flex items-center gap-4 rounded-xl border border-dashed border-[#d1e2ff] bg-[#fafbfd] p-[21px] transition-colors',
                uploadingLogo ? 'cursor-wait opacity-70' : 'cursor-pointer hover:border-brand/60',
              ].join(' ')}
            >
              {values.logoUrl ? (
                <img
                  src={values.logoUrl}
                  alt="Organization logo"
                  className="size-12 shrink-0 rounded-xl object-cover"
                />
              ) : (
                <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-brand-shade text-brand">
                  <UploadIcon className="size-6" />
                </span>
              )}
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-medium text-ink">
                  {uploadingLogo ? (
                    'Uploading…'
                  ) : values.logo ? (
                    values.logo.name
                  ) : values.logoUrl ? (
                    <>
                      <span className="font-semibold text-brand">Logo uploaded</span> — click to
                      replace
                    </>
                  ) : (
                    <>
                      <span className="font-semibold text-brand">Click to upload</span> or drag and
                      drop logo file
                    </>
                  )}
                </span>
                <span className="text-[11px] text-ink-secondary">
                  SVG, PNG, or JPG (maximum allowed size: 5MB)
                </span>
              </div>
              <input
                type="file"
                name="logo"
                accept=".svg,.png,.jpg,.jpeg"
                className="hidden"
                disabled={uploadingLogo}
                onChange={(e) => {
                  handleLogoChange(e.currentTarget.files?.[0] ?? null);
                  e.currentTarget.value = ''; // allow re-selecting the same file
                }}
              />
            </label>
          </div>
        </div>
      </Card>

      {/* Card 2 — Location & Contact */}
      <Card
        title="Location & Contact"
        subtitle="Provide official contact person details and physical premises location."
      >
        <div className="flex flex-col gap-4">
          <Field label="Address" htmlFor="address" error={errorOf('address')}>
            <textarea
              id="address"
              name="address"
              rows={3}
              placeholder="Full physical address..."
              value={values.address}
              onChange={handleChange}
              onBlur={handleBlur}
              className={textareaCls('address')}
            />
          </Field>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Contact Person" htmlFor="contactPerson" error={errorOf('contactPerson')}>
              <Input
                id="contactPerson"
                name="contactPerson"
                placeholder="Full name"
                value={values.contactPerson}
                onChange={handleChange}
                onBlur={handleBlur}
                status={invalid('contactPerson')}
              />
            </Field>
            <Field label="Phone Number" htmlFor="phone" error={errorOf('phone')}>
              <div
                className={[
                  'flex items-center rounded-lg border bg-white transition-colors focus-within:ring-2',
                  errorOf('phone')
                    ? 'border-red-400 focus-within:border-red-500 focus-within:ring-red-100'
                    : 'border-line-soft focus-within:border-brand focus-within:ring-brand/15',
                ].join(' ')}
              >
                <span className="flex shrink-0 items-center border-r border-line-soft px-3 text-sm font-medium text-ink-700">
                  +91
                </span>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="98765 43210"
                  value={values.phone}
                  onChange={(e) =>
                    setFieldValue('phone', e.target.value.replace(/\D/g, '').slice(0, 10))
                  }
                  onBlur={handleBlur}
                  className="h-11 w-full rounded-r-lg bg-transparent px-3.5 text-sm text-ink placeholder:text-[#94a3b8] focus:outline-none"
                />
              </div>
            </Field>
          </div>

          <Field label="Email Address" htmlFor="email" error={errorOf('email')}>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="org@example.gov"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              status={invalid('email')}
            />
          </Field>
        </div>
      </Card>
    </div>
  );
}
